import type { Habit, HabitLog } from '../api/habits.js';
import { addDays, isoWeekNumber, mondayOfWeek, todayInParis } from './dates.js';

export const WEEKS_WINDOW = 12;

export interface WeekStat {
  weekStart: string; // Monday, YYYY-MM-DD
  weekNum: number; // ISO week number
  count: number; // days done this week, 0-7
}

export interface WeeklyRatio {
  weekStart: string; // Monday YYYY-MM-DD
  weekNum: number; // ISO week
  count: number; // days done across the eligible habits
  possible: number; // days × eligible habits this week
  ratio: number | null; // count / possible, null when possible === 0; clamped to [0, 1]
}

export interface HabitWeeklySeries {
  habit: Habit;
  points: WeeklyRatio[]; // length === weeks; ratio === null before firstActivity
}

export interface MonthStat {
  month: string; // YYYY-MM
  year: number;
  monthNum: number; // 1-12
  count: number; // days done this month
  possible: number; // days in month, or elapsed days for the current month
}

// O(1)-lookup set of the dates a habit was done.
function datesFor(logs: HabitLog[], hid: string): Set<string> {
  const set = new Set<string>();
  for (const log of logs) {
    if (log.habitId === hid) set.add(log.date);
  }
  return set;
}

function daysInMonth(year: number, monthNum: number): number {
  return new Date(Date.UTC(year, monthNum, 0)).getUTCDate();
}

// Most recent week first.
export function weeklyCounts(
  logs: HabitLog[],
  hid: string,
  weeks: number,
  today: string = todayInParis()
): WeekStat[] {
  const done = datesFor(logs, hid);
  const currentMonday = mondayOfWeek(today);
  const out: WeekStat[] = [];
  for (let w = 0; w < weeks; w++) {
    const weekStart = addDays(currentMonday, -7 * w);
    let count = 0;
    for (let d = 0; d < 7; d++) {
      const day = addDays(weekStart, d);
      if (day > today) break; // don't count future days
      if (done.has(day)) count += 1;
    }
    out.push({ weekStart, weekNum: isoWeekNumber(weekStart), count });
  }
  return out;
}

// Most recent month first.
export function monthlyCounts(
  logs: HabitLog[],
  hid: string,
  months: number,
  today: string = todayInParis()
): MonthStat[] {
  const done = datesFor(logs, hid);
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  const out: MonthStat[] = [];
  for (let m = 0; m < months; m++) {
    // Walk back m months from the current month.
    const date = new Date(Date.UTC(ty, tm - 1 - m, 1));
    const year = date.getUTCFullYear();
    const monthNum = date.getUTCMonth() + 1; // 1-12
    const isCurrent = m === 0;
    const possible = isCurrent ? td : daysInMonth(year, monthNum);
    const prefix = `${year}-${String(monthNum).padStart(2, '0')}`;
    let count = 0;
    for (const day of done) {
      // Don't count future days — keeps count <= possible for the current month.
      if (day.startsWith(prefix) && day <= today) count += 1;
    }
    out.push({ month: prefix, year, monthNum, count, possible });
  }
  return out;
}

// Longest run of consecutive days ever, across the habit's whole history.
export function bestStreak(logs: HabitLog[], hid: string): number {
  const dates = Array.from(datesFor(logs, hid)).sort();
  let best = 0;
  let run = 0;
  let prev: string | null = null;
  for (const day of dates) {
    run = prev !== null && addDays(prev, 1) === day ? run + 1 : 1;
    if (run > best) best = run;
    prev = day;
  }
  return best;
}

export function totalDays(logs: HabitLog[], hid: string): number {
  return datesFor(logs, hid).size;
}

// Earliest date a habit could plausibly be tracked from: min of createdAt and any log.
// createdAt may be a full ISO timestamp — slice to YYYY-MM-DD so string compare is safe.
function firstActivityDate(habit: Habit, logsByHabit: Map<string, Set<string>>): string {
  let first = habit.createdAt.slice(0, 10);
  const set = logsByHabit.get(habit.habitId);
  if (set) {
    for (const date of set) {
      if (date < first) first = date;
    }
  }
  return first;
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

// Pre-index logs by habit for O(1) per-day lookups.
function indexLogs(habits: Habit[], logs: HabitLog[]): Map<string, Set<string>> {
  const byHabit = new Map<string, Set<string>>();
  for (const habit of habits) byHabit.set(habit.habitId, new Set());
  for (const log of logs) {
    const set = byHabit.get(log.habitId);
    if (set) set.add(log.date); // logs for unknown habits (deleted) are ignored
  }
  return byHabit;
}

// Bounds of the eligible window for a habit in a given week: [start, end] inclusive.
// Returns null when the habit was not yet active or the week is entirely in the future.
function eligibleWindow(
  weekStart: string,
  firstActivity: string,
  today: string
): { start: string; end: string } | null {
  const start = weekStart > firstActivity ? weekStart : firstActivity;
  const weekEnd = addDays(weekStart, 6);
  const end = weekEnd < today ? weekEnd : today;
  if (start > end) return null;
  return { start, end };
}

// Sum done days in [start, end] inclusive from a precomputed set. Caps at 7 (one week).
function countDoneInRange(done: Set<string>, start: string, end: string): { count: number; possible: number } {
  let count = 0;
  let possible = 0;
  let cursor = start;
  while (cursor <= end && possible < 7) {
    possible += 1;
    if (done.has(cursor)) count += 1;
    cursor = addDays(cursor, 1);
  }
  return { count, possible };
}

// Aggregate completion ratio per week across all habits. Most recent first.
export function aggregateWeeklyRatios(
  logs: HabitLog[],
  habits: Habit[],
  weeks: number,
  today: string = todayInParis()
): WeeklyRatio[] {
  if (habits.length === 0) return [];
  const logsByHabit = indexLogs(habits, logs);
  const firsts = new Map<string, string>();
  for (const habit of habits) firsts.set(habit.habitId, firstActivityDate(habit, logsByHabit));

  const currentMonday = mondayOfWeek(today);
  const out: WeeklyRatio[] = [];

  for (let w = 0; w < weeks; w++) {
    const weekStart = addDays(currentMonday, -7 * w);
    let totalCount = 0;
    let totalPossible = 0;

    for (const habit of habits) {
      const window = eligibleWindow(weekStart, firsts.get(habit.habitId)!, today);
      if (!window) continue;
      const { count, possible } = countDoneInRange(
        logsByHabit.get(habit.habitId)!,
        window.start,
        window.end
      );
      totalCount += count;
      totalPossible += possible;
    }

    out.push({
      weekStart,
      weekNum: isoWeekNumber(weekStart),
      count: totalCount,
      possible: totalPossible,
      ratio: totalPossible === 0 ? null : clamp01(totalCount / totalPossible),
    });
  }

  return out;
}

// One weekly series per habit, in the order of habits[]. Most recent week first.
export function perHabitWeeklyRatios(
  logs: HabitLog[],
  habits: Habit[],
  weeks: number,
  today: string = todayInParis()
): HabitWeeklySeries[] {
  if (habits.length === 0) return [];
  const logsByHabit = indexLogs(habits, logs);
  const currentMonday = mondayOfWeek(today);

  return habits.map((habit) => {
    const first = firstActivityDate(habit, logsByHabit);
    const done = logsByHabit.get(habit.habitId)!;
    const points: WeeklyRatio[] = [];

    for (let w = 0; w < weeks; w++) {
      const weekStart = addDays(currentMonday, -7 * w);
      const window = eligibleWindow(weekStart, first, today);
      if (!window) {
        points.push({
          weekStart,
          weekNum: isoWeekNumber(weekStart),
          count: 0,
          possible: 0,
          ratio: null,
        });
        continue;
      }
      const { count, possible } = countDoneInRange(done, window.start, window.end);
      points.push({
        weekStart,
        weekNum: isoWeekNumber(weekStart),
        count,
        possible,
        ratio: possible === 0 ? null : clamp01(count / possible),
      });
    }

    return { habit, points };
  });
}

// Consecutive days up to today (today counts if done, otherwise streak ends yesterday).
export function currentStreak(
  logs: HabitLog[],
  hid: string,
  today: string = todayInParis()
): number {
  const done = datesFor(logs, hid);
  let count = 0;
  let cursor = done.has(today) ? today : addDays(today, -1);
  while (done.has(cursor)) {
    count += 1;
    cursor = addDays(cursor, -1);
    if (count > 3650) break;
  }
  return count;
}

// Consecutive missed days ending yesterday — today still in progress doesn't
// count as missed. Bounded by createdAt so a fresh habit starts at 0.
export function currentMissStreak(
  logs: HabitLog[],
  hid: string,
  createdAt: string,
  today: string = todayInParis()
): number {
  const done = datesFor(logs, hid);
  if (done.has(today)) return 0;
  const createdDay = createdAt.slice(0, 10);
  let count = 0;
  let cursor = addDays(today, -1);
  while (!done.has(cursor) && cursor >= createdDay) {
    count += 1;
    cursor = addDays(cursor, -1);
    if (count > 3650) break;
  }
  return count;
}
